import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Pencil, Info } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { handleError, handleSuccess } from "@/lib/error-handler";
import { Skeleton } from "@/components/ui/skeleton";

interface EmailTemplate {
  id: string;
  template_key: string;
  template_value: string;
  description: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
}

const templateSchema = z.object({
  template_value: z.string().min(1, "Arvo on pakollinen"),
});

type TemplateFormData = z.infer<typeof templateSchema>;

// Mapping for user-friendly labels
const templateLabels: Record<string, string> = {
  // Seurakunnan hyväksyntä
  cemetery_email_subject: "Sähköpostin otsikko",
  cemetery_email_greeting: "Tervehdys",
  cemetery_default_message: "Oletusviestin pohja",
  cemetery_email_footer: "Sähköpostin footer",
  cemetery_page_title: "Hyväksyntäsivun otsikko",
  cemetery_approved_message: "Hyväksytty-viesti",
  cemetery_rejected_message: "Hylätty-viesti",
  cemetery_company_signature: "Yrityksen allekirjoitus",
  // Tilausvahvistus
  order_email_subject: "Sähköpostin otsikko",
  order_email_greeting: "Tervehdys",
  order_confirmed_message: "Vahvistusviesti",
  order_email_footer: "Sähköpostin footer",
  // Tilamuutosilmoitukset
  status_email_subject: "Sähköpostin otsikko",
  status_email_greeting: "Tervehdys",
  status_ready_message: "Valmis asennettavaksi -viesti",
  status_installed_message: "Asennettu-viesti",
  status_completed_message: "Valmis-viesti",
  status_email_footer: "Sähköpostin footer",
};

const categoryLabels: Record<string, string> = {
  cemetery_approval: "Seurakunnan hyväksyntä",
  order_confirmation: "Tilausvahvistus",
  status_notification: "Tilamuutosilmoitukset",
  general: "Yleiset",
};

export default function EmailTemplates() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      template_value: "",
    },
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .order("category", { ascending: true })
        .order("template_key", { ascending: true });

      if (error) throw error;
      return data as EmailTemplate[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, template_value }: { id: string; template_value: string }) => {
      const { error } = await supabase
        .from("email_templates")
        .update({ template_value })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      handleSuccess("Viestimalli päivitetty");
      handleClose();
    },
    onError: (error) => {
      handleError(error, { title: "Virhe tallennuksessa" });
    },
  });

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    form.reset({
      template_value: template.template_value,
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingTemplate(null);
    form.reset();
  };

  const onSubmit = (data: TemplateFormData) => {
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, template_value: data.template_value });
    }
  };

  // Group templates by category
  const groupedTemplates = templates?.reduce((acc, template) => {
    const category = template.category || "general";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, EmailTemplate[]>);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Viestimallit</h1>
            <p className="text-muted-foreground mt-1">
              Hallitse sähköpostien ja hyväksyntäsivujen tekstejä
            </p>
          </div>
        </div>

        {/* Placeholder Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Käytettävissä olevat muuttujat
            </CardTitle>
            <CardDescription>
              Voit käyttää näitä muuttujia viestimalleissa. Ne korvataan automaattisesti oikeilla arvoilla.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-primary">{"{contact_name}"}</code>
                <p className="text-xs text-muted-foreground mt-1">Yhteyshenkilön nimi</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-primary">{"{order_number}"}</code>
                <p className="text-xs text-muted-foreground mt-1">Tilausnumero</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-primary">{"{cemetery_name}"}</code>
                <p className="text-xs text-muted-foreground mt-1">Hautausmaan nimi</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-primary">{"{customer_name}"}</code>
                <p className="text-xs text-muted-foreground mt-1">Asiakkaan nimi</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono text-primary">{"{status}"}</code>
                <p className="text-xs text-muted-foreground mt-1">Tilauksen tila</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates by Category */}
        {isLoading ? (
          <Card>
            <CardContent className="pt-6 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </CardContent>
          </Card>
        ) : !templates || templates.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-8 text-muted-foreground">
              <p>Ei viestimalleja.</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedTemplates || {}).map(([category, categoryTemplates]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {categoryLabels[category] || category}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Malli</TableHead>
                      <TableHead>Nykyinen arvo</TableHead>
                      <TableHead className="w-[100px]">Toiminnot</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {templateLabels[template.template_key] || template.template_key}
                            </div>
                            {template.description && (
                              <div className="text-xs text-muted-foreground">
                                {template.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground max-w-md truncate">
                            {template.template_value.substring(0, 100)}
                            {template.template_value.length > 100 ? "..." : ""}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(template)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
        )}

        {/* Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Muokkaa: {editingTemplate && (templateLabels[editingTemplate.template_key] || editingTemplate.template_key)}
              </DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="template_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arvo</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={8}
                          placeholder="Kirjoita viestimalli..."
                          className="font-mono text-sm"
                        />
                      </FormControl>
                      <FormDescription>
                        Käytä muuttujia: {"{contact_name}"}, {"{order_number}"}, {"{cemetery_name}"}, {"{customer_name}"}, {"{status}"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Peruuta
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? "Tallennetaan..." : "Tallenna"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
